import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import type { ChartData, ChartOptions, ScriptableContext } from "chart.js";
import type { AdminSeriesPoint } from "../../../graphql/types";
import { formatNumberDe } from "../../../utils/format";
import { CHART_COLORS } from "../chartTheme";
import { createHatchPattern } from "../chartHelper";
import { ChartCard } from "./ChartCard";
import "../chartSetup";
import "./SourceBarCard.css";

interface SourceBarCardProps {
  organic: AdminSeriesPoint[] | null;
  loading: boolean;
}

/**
 * Registration-source bar chart (FE-3): solid orange "Organisch" bars with a
 * small hatched "Referral · geplant" cap. Referral is 0 for now (referralPlanned)
 * — the cap is a fixed visual placeholder so the planned segment reads on the
 * legend and tooltip, while the tooltip states "Referral: 0 (geplant)".
 */
export const SourceBarCard: React.FC<SourceBarCardProps> = ({ organic, loading }) => {
  const { t } = useTranslation();

  const points = useMemo(() => organic ?? [], [organic]);
  // Fixed cap height (~6% of the tallest bar) purely to visualize the planned segment.
  const plannedCap = useMemo(() => {
    const max = points.reduce((m, p) => Math.max(m, p.value), 0);
    return Math.max(1, Math.round(max * 0.06));
  }, [points]);

  const data = useMemo<ChartData<"bar">>(
    () => ({
      labels: points.map((p) => p.label),
      datasets: [
        {
          label: "organic",
          data: points.map((p) => p.value),
          backgroundColor: CHART_COLORS.accent,
          borderRadius: 4,
          stack: "source",
          maxBarThickness: 34,
        },
        {
          label: "planned",
          data: points.map(() => plannedCap),
          backgroundColor: (ctx: ScriptableContext<"bar">) => {
            const c = ctx.chart.ctx;
            return createHatchPattern(c) ?? CHART_COLORS.accentArea;
          },
          borderRadius: 4,
          stack: "source",
          maxBarThickness: 34,
        },
      ],
    }),
    [points, plannedCap],
  );

  const options = useMemo<ChartOptions<"bar">>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: CHART_COLORS.tooltipBg,
          borderColor: CHART_COLORS.tooltipBorder,
          borderWidth: 1,
          titleColor: CHART_COLORS.tick,
          bodyColor: CHART_COLORS.tooltipText,
          padding: 10,
          cornerRadius: 10,
          displayColors: false,
          callbacks: {
            // Only show the "Organisch: N" + "Referral: 0 (geplant)" pair once per bar.
            label: (ctx) =>
              ctx.datasetIndex === 0
                ? t("dashboard.charts.tooltipOrganic", { value: formatNumberDe(ctx.parsed.y ?? 0) })
                : t("dashboard.charts.tooltipReferral"),
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          border: { display: false },
          ticks: { color: CHART_COLORS.tick, font: { size: 11 }, maxTicksLimit: 8, autoSkip: true },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { color: CHART_COLORS.grid },
          border: { display: false },
          ticks: {
            color: CHART_COLORS.tick,
            font: { size: 11 },
            maxTicksLimit: 5,
            callback: (v) => formatNumberDe(Number(v)),
          },
        },
      },
    }),
    [t],
  );

  const legend = (
    <div className="chart-legend">
      <span className="chart-legend-item">
        <span className="chart-legend-swatch chart-legend-swatch--solid" />
        {t("dashboard.charts.legendOrganic")}
      </span>
      <span className="chart-legend-item">
        <span className="chart-legend-swatch chart-legend-swatch--hatched" />
        {t("dashboard.charts.legendReferral")}
      </span>
    </div>
  );

  return (
    <ChartCard title={t("dashboard.charts.source")} headerRight={legend}>
      {!organic && loading ? (
        <div className="lyra-skeleton chart-skeleton" />
      ) : (
        <Bar data={data} options={options} />
      )}
    </ChartCard>
  );
};
